import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import { competitionAverageSelectedCompetitionsAtom } from '@/atoms/filters/team/teamCompetitionAverageFilters';
import { unwrappedComparisonTeamNameAtom } from '@/atoms/queries/team/teamComparison';
import { teamRenderedComparisonSourceAtom } from '@/atoms/queries/team/teamLeagueAverageRadar';
import {
  renderedRadarDataAtom,
  unwrappedRadarDataFormatAtom,
  unwrappedRadarTableComparisonPercentilesAtom,
  unwrappedRadarTableMetricPercentilesAtom,
} from '@/atoms/radar';
import { unwrappedSelectedTeamNameAtom } from '@/atoms/team/info';
import { teamIdAtom } from '@/atoms/team/team';
import { RadarComparisonLegend, RadarLegendItem } from '@/components/radar/RadarComparisonLegend';
import { useIsCompetitionAverageMode } from '@/hooks/useIsCompetitionAverageMode';
import { RadarDataFormat } from '@/types/radar';
import { toPercentileRadarData, toRadarVisData } from '@/utils/radar';

export const TeamComparisonLegend = ({ id }: { id: string }) => {
  const { t } = useTranslation(['metrics', 'radar']);
  const teamId = useAtomValue(teamIdAtom);
  const teamName = useAtomValue(unwrappedSelectedTeamNameAtom);
  const renderedRadarData = useAtomValue(renderedRadarDataAtom);
  const entityComparisonName = useAtomValue(unwrappedComparisonTeamNameAtom);
  const { data: renderedComparisonData } = useAtomValue(teamRenderedComparisonSourceAtom);
  const metricPercentiles = useAtomValue(unwrappedRadarTableMetricPercentilesAtom);
  const comparisonPercentiles = useAtomValue(unwrappedRadarTableComparisonPercentilesAtom);
  const radarDataFormat = useAtomValue(unwrappedRadarDataFormatAtom);
  const isPercentileMode = radarDataFormat === RadarDataFormat.Percentiles;
  const isCompetitionAverageMode = useIsCompetitionAverageMode();
  const competitionAverageCompetitions = useAtomValue(competitionAverageSelectedCompetitionsAtom);

  const competitionAverageName =
    competitionAverageCompetitions[0]?.label ?? t('competitionAverage.optionLabel', { ns: 'radar' });
  const comparisonName = isCompetitionAverageMode ? competitionAverageName : entityComparisonName;

  const primaryData = isPercentileMode
    ? toPercentileRadarData(renderedRadarData, metricPercentiles)
    : renderedRadarData;
  const comparisonData = isPercentileMode
    ? toPercentileRadarData(renderedComparisonData, comparisonPercentiles)
    : renderedComparisonData;

  const radars: [RadarLegendItem, RadarLegendItem] = [
    { id: `team-${teamId}-${id}`, name: teamName, data: toRadarVisData(t, primaryData) },
    { id: `comparison-team-${id}`, name: comparisonName, data: toRadarVisData(t, comparisonData) },
  ];

  return <RadarComparisonLegend radars={radars} />;
};
